import {
  type AssistantContent,
  convertToModelMessages,
  jsonSchema,
  type ModelMessage,
  isStepCount,
  streamText,
  toUIMessageStream,
  tool,
  type ToolContent,
  type ToolSet,
  type UIMessageStreamWriter,
} from 'ai';

import { type Subagent } from './mcpClient';
import { dixaGateway } from './provider';
import { type MimUIMessage } from './structuredResponse';
import { toTraceparent } from './traceparent';
import { toErrorText } from './transport';

type SubagentInput = {
  query: string;
};

/** stopWhen defaults to 1,
 *  which means that it stops immediately after processing the strucuted output.
 *  Set to 10 so that we have some "budget" to call tools.
 *  See https://ai-sdk.dev/docs/reference/ai-sdk-core/stream-text#stop-when
 */
const STEP_COUNT = 10;

const inputSchema = jsonSchema<SubagentInput>({
  type: 'object',
  properties: {
    query: {
      type: 'string',
      description: 'Plain-English question for the subagent to answer.',
    },
  },
  required: ['query'],
});

type ContentPart =
  | Exclude<AssistantContent, string>[number]
  | ToolContent[number];

const toolResultIds = (content: readonly ContentPart[]): string[] =>
  content.flatMap((part) =>
    part.type === 'tool-result' ? [part.toolCallId] : [],
  );

const toolCallIds = (content: readonly ContentPart[]): string[] =>
  content.flatMap((part) =>
    part.type === 'tool-call' ? [part.toolCallId] : [],
  );

const hasForeignToolCall = (
  content: readonly ContentPart[],
  names: ReadonlySet<string>,
): boolean =>
  content.some(
    (part) => part.type === 'tool-call' && !names.has(part.toolName),
  );

const answeredToolCallIds = (
  messages: readonly ModelMessage[],
): ReadonlySet<string> =>
  new Set(
    messages.flatMap((message) => {
      if (message.role === 'tool') return toolResultIds(message.content);
      if (message.role !== 'assistant' || typeof message.content === 'string') {
        return [];
      }

      return toolResultIds(message.content);
    }),
  );

type ToolScope = {
  names: ReadonlySet<string>;
  answered: ReadonlySet<string>;
  dropped: ReadonlySet<string>;
};

const isVisiblePart = (part: ContentPart, scope: ToolScope): boolean => {
  if (part.type === 'tool-call') {
    return (
      scope.names.has(part.toolName) && scope.answered.has(part.toolCallId)
    );
  }

  if (part.type === 'tool-result') {
    return (
      scope.names.has(part.toolName) && !scope.dropped.has(part.toolCallId)
    );
  }

  return true;
};

type OwnView = {
  names: ReadonlySet<string>;
  self: string;
  runs: ModelMessage[][];
};

/**
 * Only pass the tool calls that the subagent can make itself, putting each
 * earlier run back where its dispatch stood so the order survives the turn.
 */
const keepOwnToolCalls = (
  messages: readonly ModelMessage[],
  view: OwnView,
): ModelMessage[] => {
  // Registered as messages are dropped, which their results always follow.
  const dropped = new Set<string>();
  const scope: ToolScope = {
    names: view.names,
    answered: answeredToolCallIds(messages),
    dropped,
  };
  const isVisible = (part: ContentPart): boolean => isVisiblePart(part, scope);
  // Dispatches and runs are both chronological, so they pair off in order.
  const pending = view.runs.slice();

  return messages.flatMap<ModelMessage>((message) => {
    if (message.role === 'tool') {
      const content = message.content.filter(isVisible);

      return content.length > 0 ? [{ ...message, content }] : [];
    }

    if (message.role === 'assistant' && typeof message.content !== 'string') {
      // Check if the tool call associated with the assistant belongs to the subagent
      if (hasForeignToolCall(message.content, view.names)) {
        const isSelf = message.content.some(
          (part) => part.type === 'tool-call' && part.toolName === view.self,
        );
        toolCallIds(message.content).forEach((id) => dropped.add(id));

        return isSelf ? (pending.shift() ?? []) : [];
      }

      const content = message.content.filter(isVisible);

      return content.length > 0 ? [{ ...message, content }] : [];
    }

    return [message];
  });
};

const isToolPart = (part: MimUIMessage['parts'][number]): boolean =>
  part.type === 'dynamic-tool' || part.type.startsWith('tool-');

/** Tool parts a subagent streamed, one group per run of its own markers. */
const ownToolRuns = (
  history: readonly MimUIMessage[],
  name: string,
): MimUIMessage['parts'][] => {
  const runs: MimUIMessage['parts'][] = [];
  let current: MimUIMessage['parts'] | undefined;

  history.forEach((message) =>
    message.parts.forEach((part) => {
      if (part.type === 'data-subagent-open') {
        if (part.data === name) current = [];

        return;
      }

      if (part.type === 'data-subagent-close') {
        if (part.data === name && current) {
          runs.push(current);
          current = undefined;
        }

        return;
      }

      if (current && isToolPart(part)) current.push(part);
    }),
  );

  return runs;
};

const toModelMessages = async (
  parts: MimUIMessage['parts'],
): Promise<ModelMessage[]> => {
  if (parts.length === 0) return [];

  try {
    return await convertToModelMessages<MimUIMessage>(
      [{ role: 'assistant', parts }],
      { ignoreIncompleteToolCalls: true },
    );
  } catch {
    return [];
  }
};

/** Its own tool calls, which the transport keeps out of the parent messages. */
const ownHistory = async (
  history: readonly MimUIMessage[],
  name: string,
): Promise<ModelMessage[][]> =>
  Promise.all(ownToolRuns(history, name).map(toModelMessages));

export const createSubagentTool = (
  subagent: Subagent,
  chatId: string,
  writer: UIMessageStreamWriter<MimUIMessage>,
  history: readonly MimUIMessage[],
  page: { tools: ToolSet; brief?: string },
) =>
  tool<SubagentInput, string, Record<string, unknown>>({
    description: subagent.description,
    inputSchema,
    metadata: { stateCopy: subagent.stateCopy ?? {} },
    execute: async ({ query }, { abortSignal, messages: chat }) => {
      // subagent should never sees itself being called.
      const tools = { ...page.tools, ...subagent.tools };
      const names = new Set(Object.keys(tools));
      const runs = await ownHistory(history, subagent.name);
      const messages: ModelMessage[] = [
        ...keepOwnToolCalls(chat, {
          names,
          self: subagent.name,
          runs,
        }),
        {
          role: 'user',
          content: page.brief ? `${page.brief}\n\n${query}` : query,
        },
      ];
      const result = streamText({
        model: dixaGateway(subagent.prompt),
        messages,
        tools,
        abortSignal,
        headers: { traceparent: toTraceparent(chatId) },
        stopWhen: isStepCount(STEP_COUNT),
      });

      const boundaryId = `${subagent.name}-${crypto.randomUUID()}`;
      writer.write({
        type: 'data-subagent-open',
        id: boundaryId,
        data: subagent.name,
      });
      writer.merge(
        toUIMessageStream({
          stream: result.stream,
          onError: toErrorText,
          sendStart: false,
          sendFinish: false,
        }),
      );

      const text = await result.text;
      writer.write({
        type: 'data-subagent-close',
        id: boundaryId,
        data: subagent.name,
      });
      return text;
    },
  });
